import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ReactSelect from 'components/common/react-select/ReactSelect';
import RouteLink from 'components/common/route-link/RouteLink';
import EditPanelLabel from 'app/editor/report/content-editor/block-edit-panel/edit-panel-components/EditPanelLabel';
import { FormComponentValue } from 'components/common/form/layout/control';
import { FormRendererAPIType } from 'components/common/form';

type PrimaryResourceType = 'dataset' | 'domain';

interface PrimaryResourcePickerProps {
    form: FormRendererAPIType;
    t: (key: string, params?: Record<string, unknown>) => string;
    primaryLabel: string;
    primaryDatasetId: number | null;
    primaryCandidates: Array<FormComponentValue>;
    chosenPrimary: string | null;
}

const typeOf = (key: string): PrimaryResourceType => (key.startsWith('domain:') ? 'domain' : 'dataset');

export default function PrimaryResourcePicker({
    form,
    t,
    primaryLabel,
    primaryDatasetId,
    primaryCandidates,
    chosenPrimary,
}: PrimaryResourcePickerProps) {
    const [activeType, setActiveType] = useState<PrimaryResourceType>('dataset');
    const [datasetKey, setDatasetKey] = useState('');
    const [domainKey, setDomainKey] = useState('');
    const hydrated = useRef(false);

    // Hydrate from the stored report-level choice once per panel open — component.settings never
    // carries primary_resource back (the server strips it) — but an Apply earlier this same
    // editing session (no full report Save yet) already left it on the form via component.settings,
    // and that in-flight pick must win over the server's last-saved chosenPrimary.
    useEffect(() => {
        if (hydrated.current) {
            return;
        }
        hydrated.current = true;
        const inFlight = (form.hookFormGetValues('primary_resource') as string) || '';
        const seed = inFlight || chosenPrimary;
        if (!seed) {
            return;
        }
        const type = typeOf(seed);
        setActiveType(type);
        if (type === 'domain') {
            setDomainKey(seed);
        } else {
            setDatasetKey(seed);
        }
    }, [chosenPrimary]);

    const activeKey = activeType === 'dataset' ? datasetKey : domainKey;

    // An empty activeKey only means "the active type has no pick yet" (e.g. the pill was just
    // switched) — it must never clobber an already-valid choice on the form. Only an actual
    // selection (non-empty) is allowed to overwrite primary_resource.
    useEffect(() => {
        if (!hydrated.current || '' === activeKey) {
            return;
        }
        if (form.hookFormGetValues('primary_resource') === activeKey) {
            return;
        }
        form.hookFormSetValue('primary_resource', activeKey, { shouldDirty: true });
    }, [activeKey]);

    const optionsByType = (type: PrimaryResourceType) =>
        primaryCandidates.filter((c) => c.props?.resource_type === type);

    const handleSelect = (value: string | string[]) => {
        const key = (Array.isArray(value) ? value[0] : value) ?? '';
        if (activeType === 'dataset') {
            setDatasetKey(key);
        } else {
            setDomainKey(key);
        }
    };

    return (
        <Stack spacing={1}>
            <EditPanelLabel>{t('concierge_primary_data_resource')}</EditPanelLabel>
            <Alert severity="warning" variant="standard">
                <strong>{primaryLabel}</strong> {t('concierge_insight_primary_not_in_kg_warning')}
                {primaryDatasetId && (
                    <>
                        {' '}
                        <RouteLink to={`/editor/dataset/${primaryDatasetId}#concierge`}>
                            {t('concierge_insight_primary_add_to_kg_link')}
                        </RouteLink>
                        .
                    </>
                )}
            </Alert>
            <Box>
                <ToggleButtonGroup
                    color="primary"
                    exclusive
                    value={activeType}
                    onChange={(_e, v: PrimaryResourceType | null) => v && setActiveType(v)}
                    data-test="primary_resource_type"
                >
                    <ToggleButton value="dataset" data-test="primary_resource_type_dataset">
                        {t('concierge_dataset')}
                    </ToggleButton>
                    <ToggleButton value="domain" data-test="primary_resource_type_domain">
                        {t('concierge_mcp_domain')}
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>
            <ReactSelect
                data={optionsByType(activeType)}
                value={activeKey}
                update={handleSelect}
                name="primary_resource_picker"
                isMulti={false}
                isClearable={false}
                dataTest="primary_resource_picker"
                placeholder={t('concierge_insight_primary_picker_ph')}
            />
        </Stack>
    );
}
